import { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import React from "react";
import { WorkflowInfoView } from "@tiny/api";
import { usePutWorkflowsFindFilterQuery } from "@tiny/api";
import { AutocompleteField, FormItem } from "@tiny/components";
import useDebounce from "@tiny/components/src/hooks/useDebounce";

interface WorkflowsValueProps {
  prefixName: string;
  disabled?: boolean;
}

function WorkflowsValue(props: WorkflowsValueProps) {
  const { prefixName, disabled } = props;

  const name = `${prefixName}.value.value`;

  return (
    <FormItem
      name={name}
      rules={{
        required: {
          value: true,
          message: "Required field",
        },
      }}
      watchValue
    >
      <WorkflowsInput disabled={disabled} />
    </FormItem>
  );
}

interface WorkflowsInputProps {
  value?: string[];
  onChangeValue?: (value: string[]) => void;
  helperText?: string;
  error?: boolean;
  inputRef?: React.ForwardedRef<HTMLInputElement>;
  disabled?: boolean;
  multiple?: boolean;
}

function WorkflowsInput(props: WorkflowsInputProps) {
  const {
    value,
    onChangeValue,
    helperText,
    error,
    inputRef,
    disabled,
    multiple = true,
  } = props;

  const [internalWorkflowValue, setInternalWorkflowValue] =
    React.useState<WorkflowInfoView[]>();

  const [searching, setSearching] = React.useState(false);

  const [search, setSearch] = React.useState("");

  const searchDebounced = useDebounce(search, 500);

  const { data: { content: workflowValue = [] } = {} } =
    usePutWorkflowsFindFilterQuery(
      {
        ids: value,
      },
      {},
      {
        enabled: !!value && value.length > 0 && !internalWorkflowValue,
      }
    );

  const { data: { content: searchResult = [] } = {}, isLoading } =
    usePutWorkflowsFindFilterQuery(
      {
        searchTextName: searchDebounced,
      },
      {
        page: 0,
        size: 10,
      },
      {
        enabled: searching,
      }
    );

  const changeInput = (
    _event: React.ChangeEvent<{}>,
    v: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (searching) {
      if (reason === "input") {
        setSearch(v);
      }
    } else {
      setSearch(v);
    }
  };

  const changeValue = (v: WorkflowInfoView[]) => {
    if (multiple) {
      setInternalWorkflowValue(v);
      const ids = v?.map((item) => item.id!);
      onChangeValue?.(ids);
    } else {
      setInternalWorkflowValue(v);
      onChangeValue?.([(v as unknown as { id: string })?.id]);
    }
  };

  const open = () => {
    setSearching(true);
  };

  const close = () => {
    setSearching(false);
  };

  return (
    <AutocompleteField
      inputRef={inputRef}
      multiple={multiple}
      loading={isLoading}
      options={searchResult}
      inputValue={search}
      value={internalWorkflowValue || workflowValue}
      onInputChange={changeInput}
      onChangeValue={changeValue}
      onOpen={open}
      onClose={close}
      getOptionLabel={(option) => option?.name}
      limitTags={1}
      helperText={helperText}
      error={error}
      showHelperTextMargin
      disabled={disabled}
      getOptionSelected={(option, value) => {
        return option?.id === value?.id;
      }}
    />
  );
}

export default WorkflowsValue;
export { WorkflowsInput };
